import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import type { ReplacementChainNode } from '@/services/deque/replacementChainApi';

interface VehicleReplacementChainBarProps {
  nodes: ReplacementChainNode[];
  currentIndex: number;
  compact?: boolean;
  onOpenVehicle?: (registrationNumber: string, vehicleId: number) => void;
}

export default function VehicleReplacementChainBar({
  nodes,
  currentIndex,
  compact = false,
  onOpenVehicle,
}: VehicleReplacementChainBarProps) {
  const { t } = useTranslation();

  if (nodes.length <= 1) return null;

  return (
    <div
      className={`flex flex-wrap items-center gap-1 ${compact ? 'mt-1' : 'mt-3'} max-w-full overflow-x-auto`}
      aria-label={t('replacementChain.title')}
    >
      {nodes.map((node, index) => {
        const isCurrent = index === currentIndex;
        const canOpen = node.vehicleId != null && onOpenVehicle != null;

        return (
          <div key={`${node.registrationNumber}-${index}`} className="flex items-center gap-1 shrink-0">
            {index > 0 && (
              <ChevronRightIcon className={`${compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} text-white/50`} />
            )}
            {canOpen ? (
              <button
                type="button"
                onClick={() => onOpenVehicle!(node.registrationNumber, node.vehicleId!)}
                className={`rounded px-2 py-0.5 font-semibold tracking-wider transition-colors ${
                  isCurrent
                    ? 'bg-white text-primary text-base'
                    : 'bg-white/15 hover:bg-white/25 text-white text-sm'
                }`}
                title={t('replacementChain.openVehicle')}
              >
                {node.registrationNumber}
              </button>
            ) : (
              <span
                className={`rounded px-2 py-0.5 tracking-wider ${
                  isCurrent
                    ? 'bg-white text-primary font-bold text-base'
                    : 'bg-white/10 text-white/70 text-sm'
                }`}
                title={t('replacementChain.notInSystem')}
              >
                {node.registrationNumber}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
